'use client';

import { useMemo } from 'react';
import { useTheme } from 'next-themes';

interface EnvironmentRendererProps {
  environmentType: string;
}

export default function EnvironmentRenderer({ environmentType }: EnvironmentRendererProps) {
  const { theme } = useTheme();

  const colors = useMemo(
    () => ({
      wall: theme === 'dark' ? '#374151' : '#f8fafc',
      furniture: {
        primary: theme === 'dark' ? '#6366f1' : '#8b5cf6',
        secondary: theme === 'dark' ? '#4338ca' : '#6366f1',
        accent: theme === 'dark' ? '#059669' : '#10b981',
        warning: theme === 'dark' ? '#dc2626' : '#ef4444',
        surface: theme === 'dark' ? '#4b5563' : '#6b7280',
      },
    }),
    [theme],
  );

  const Environment = useMemo(() => {
    switch (environmentType) {
      case 'home':
        return <HomeEnvironment colors={colors} />;
      case 'supermarket':
        return <SupermarketEnvironment colors={colors} />;
      case 'office':
        return <OfficeEnvironment colors={colors} />;
      case 'warehouse':
        return <WarehouseEnvironment colors={colors} />;
      default:
        return <HomeEnvironment colors={colors} />;
    }
  }, [environmentType, colors]);

  return (
    <group>
      {/* Floor */}
      <mesh position={[0, 0, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[12, 12]} />
        <meshStandardMaterial color={theme === 'dark' ? '#1f2937' : '#f1f5f9'} />
      </mesh>
      {Environment}
    </group>
  );
}

function HomeEnvironment({ colors }: { colors: any }) {
  return (
    <group>
      {/* Walls */}
      <mesh position={[0, 1.5, -4]}>
        <boxGeometry args={[8, 3, 0.2]} />
        <meshStandardMaterial color={colors.wall} />
      </mesh>
      <mesh position={[0, 1.5, 4]}>
        <boxGeometry args={[8, 3, 0.2]} />
        <meshStandardMaterial color={colors.wall} />
      </mesh>
      <mesh position={[-4, 1.5, 0]}>
        <boxGeometry args={[0.2, 3, 8]} />
        <meshStandardMaterial color={colors.wall} />
      </mesh>
      <mesh position={[4, 1.5, 0]}>
        <boxGeometry args={[0.2, 3, 8]} />
        <meshStandardMaterial color={colors.wall} />
      </mesh>

      {/* Furniture */}
      <mesh position={[2, 0.4, 1]}>
        <boxGeometry args={[1.5, 0.8, 0.8]} />
        <meshStandardMaterial color={colors.furniture.primary} />
      </mesh>
      <mesh position={[-2, 0.3, 3]}>
        <boxGeometry args={[1.8, 0.6, 1.2]} />
        <meshStandardMaterial color={colors.furniture.secondary} />
      </mesh>
      <mesh position={[-1, 0.5, -1]}>
        <boxGeometry args={[2, 1, 0.6]} />
        <meshStandardMaterial color={colors.furniture.accent} />
      </mesh>
    </group>
  );
}

function SupermarketEnvironment({ colors }: { colors: any }) {
  return (
    <group>
      {/* Shelving units */}
      <mesh position={[-3, 1, 2]}>
        <boxGeometry args={[1, 2, 0.3]} />
        <meshStandardMaterial color={colors.furniture.accent} />
      </mesh>
      <mesh position={[3, 1, 2]}>
        <boxGeometry args={[1, 2, 0.3]} />
        <meshStandardMaterial color={colors.furniture.primary} />
      </mesh>
      <mesh position={[0, 1, -3]}>
        <boxGeometry args={[2, 2, 0.4]} />
        <meshStandardMaterial color={colors.furniture.warning} />
      </mesh>

      {/* Checkout counters */}
      <mesh position={[0, 0.5, 3]}>
        <boxGeometry args={[3, 1, 0.8]} />
        <meshStandardMaterial color={colors.furniture.surface} />
      </mesh>
    </group>
  );
}

function OfficeEnvironment({ colors }: { colors: any }) {
  return (
    <group>
      {/* Desks */}
      <mesh position={[2, 0.4, 0]}>
        <boxGeometry args={[2, 0.8, 1]} />
        <meshStandardMaterial color={colors.furniture.primary} />
      </mesh>

      {/* Conference table */}
      <mesh position={[-2, 0.4, 1]}>
        <boxGeometry args={[1.5, 0.8, 2]} />
        <meshStandardMaterial color={colors.furniture.secondary} />
      </mesh>

      {/* Server racks */}
      <mesh position={[0, 1, -3]}>
        <boxGeometry args={[0.6, 2, 0.8]} />
        <meshStandardMaterial color={colors.furniture.surface} />
      </mesh>
    </group>
  );
}

function WarehouseEnvironment({ colors }: { colors: any }) {
  return (
    <group>
      {/* Storage racks */}
      <mesh position={[2, 2, 2]}>
        <boxGeometry args={[1, 4, 0.5]} />
        <meshStandardMaterial color={colors.furniture.surface} />
      </mesh>
      <mesh position={[-2, 2, 2]}>
        <boxGeometry args={[1, 4, 0.5]} />
        <meshStandardMaterial color={colors.furniture.surface} />
      </mesh>

      {/* Loading dock */}
      <mesh position={[-3, 0.5, -2]}>
        <boxGeometry args={[1, 1, 2]} />
        <meshStandardMaterial color={colors.furniture.accent} />
      </mesh>
    </group>
  );
}
